// Shared presentation rules for a school class, so the class list and the class
// detail page cannot drift apart on badges or status wording.

export const formatTrack = (track) => (track === "BMS" ? "BMS" : "Vocational");

export const getTrackBadgeClasses = (track) =>
  track === "BMS"
    ? "bg-sunrise-red/10 text-sunrise-red border border-sunrise-red/30"
    : "bg-slate-cloud/10 text-slate-cloud border border-slate-cloud/30";

export const CLASS_STATUS = {
  UPCOMING: "UPCOMING",
  ACTIVE: "ACTIVE",
  ARCHIVED: "ARCHIVED",
  INACTIVE: "INACTIVE",
};

export const todayIso = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

/**
 * A class runs on its own dates: it is upcoming until its start date, active in
 * between, and archived once its end date has passed. The stored `isActive`
 * flag is a manual override that can switch a class off ahead of time.
 *
 * ISO dates compare correctly as strings, so no Date parsing is needed.
 */
export const getClassStatus = (classItem, today = todayIso()) => {
  if (!classItem) return CLASS_STATUS.INACTIVE;
  if (classItem.isActive === false) return CLASS_STATUS.INACTIVE;

  const start = classItem.startDate
    ? String(classItem.startDate).slice(0, 10)
    : null;
  const end = classItem.endDate ? String(classItem.endDate).slice(0, 10) : null;

  if (end && end < today) return CLASS_STATUS.ARCHIVED;
  if (start && start > today) return CLASS_STATUS.UPCOMING;
  return CLASS_STATUS.ACTIVE;
};

export const isArchivedClass = (classItem, today = todayIso()) =>
  getClassStatus(classItem, today) === CLASS_STATUS.ARCHIVED;

export const getClassStatusPresentation = (status) => {
  switch (status) {
    case CLASS_STATUS.ACTIVE:
      return {
        label: "Active",
        icon: "fa-circle-play",
        hint: "This class is running right now.",
        classes:
          "bg-green-cloud/10 text-green-cloud border border-green-cloud/30",
      };
    case CLASS_STATUS.UPCOMING:
      return {
        label: "Starts soon",
        icon: "fa-hourglass-start",
        // An upcoming class stays in its school block on purpose: it can be
        // filled with students before the first day of school.
        hint: "This class has not started yet. Students can already be added.",
        classes: "bg-amber-50 text-amber-700 border border-amber-200",
      };
    case CLASS_STATUS.ARCHIVED:
      return {
        label: "Archived",
        icon: "fa-box-archive",
        hint: "The end date of this class has passed.",
        classes: "bg-gray-100 text-gray-600 border border-gray-300",
      };
    default:
      return {
        label: "Inactive",
        icon: "fa-circle-pause",
        hint: "This class was switched off manually.",
        classes: "bg-gray-100 text-gray-600 border border-gray-300",
      };
  }
};
